/**
 * Map markers (D800): the Vello Design System's "Marcadores no mapa" (`guidelines/map-markers.html`,
 * `assets/map/markers/manifest.json` of Claude Design project 00a6290b): the kit's "lollipop" family — a disc on a stem with a
 * ringed base — for the pickup ("embarque": navy disc, white ring, the ONLY green, a #10C554 core), the destination ("destino":
 * a navy/white chequered disc, no red — the chequer marks the arrival) and the numbered stops ("parada": a rounded square with a
 * tail). Each has a light variant (navy body, white halo) for the light map and a dark one (white body, #0E1626 halo) for the
 * dark map. Sizes: 44 px tall on the map (stops 30), 32 zoomed out (stops 22), 56 selected; anchored at the base of the stem
 * (the stop at its tail's tip).
 *
 * The same art without its stem — the bare disc / square, `head: true` — is the route marker of lists and address rows
 * (owner, 2026-10-28: "os marcadores puros sem o pininho por embaixo"), replacing the green circle and red square.
 *
 * This module is pure strings, so a native app (`react-native-svg` `SvgXml`) and the web (an `<img>` data URI) draw the very
 * same markup. It reproduces the design's `embarque-*`, `destino-*` and `parada-1…3-*` SVG element for element (imported
 * 2026-10-28 through the Claude Design MCP; only their C2PA metadata block is left out); `parada-n` takes any number. Brand
 * hex lives here, never in app code.
 */
export const MAP_MARKER_KINDS = ['pickup', 'dropoff', 'stop'];
/** The design's file name of each kind (`assets/map/markers/<name>-<theme>.svg`). */
export const MAP_MARKER_FILE_NAMES = { pickup: 'embarque', dropoff: 'destino', stop: 'parada' };
const PALETTES = {
    light: { body: '#233048', halo: '#FFFFFF', core: '#FFFFFF', checkGround: '#FFFFFF', checkSquare: '#233048', digit: '#FFFFFF' },
    dark: { body: '#FFFFFF', halo: '#0E1626', core: '#233048', checkGround: '#233048', checkSquare: '#FFFFFF', digit: '#233048' },
};
/** The pickup's core: the one green of the map markers. */
const PICKUP_GREEN = '#10C554';
/** Design box (px) of each kind with its stem, and of the bare head. */
export const MAP_MARKER_BOX = {
    pickup: { width: 32, height: 44 },
    dropoff: { width: 32, height: 44 },
    stop: { width: 24, height: 30 },
};
export const MAP_MARKER_HEAD_BOX = {
    pickup: { width: 24, height: 24 },
    dropoff: { width: 24, height: 24 },
    stop: { width: 21.5, height: 21.5 },
};
export const MAP_MARKER_STATES = ['normal', 'zoomed-out', 'selected'];
/** Rendered height (px / dp) by state; the width keeps the box's aspect. Stops have their own scale. */
export function mapMarkerHeight(kind, state = 'normal') {
    if (kind === 'stop')
        return state === 'zoomed-out' ? 22 : 30;
    return state === 'zoomed-out' ? 32 : state === 'selected' ? 56 : 44;
}
/** Where the marker touches its coordinate, as fractions of its box (base of the stem; the stop's tail tip). */
export function mapMarkerAnchor(kind) {
    return kind === 'stop' ? { x: 0.5, y: 26.5 / 30 } : { x: 0.5, y: 43 / 44 };
}
const DESIGN_FONT = "'Hanken Grotesk',system-ui,sans-serif";
/** The stem, its ringed base and its shadow (pickup and destination). */
function stem(p) {
    return (`<ellipse cx="16" cy="42" rx="6" ry="1.8" fill="#000" opacity=".14"></ellipse>\n` +
        `<rect x="14.8" y="21" width="2.4" height="16" rx="1.2" fill="${p.body}" stroke="${p.halo}" stroke-width="1.5" paint-order="stroke"></rect>\n` +
        `<circle cx="16" cy="39" r="3.6" fill="${p.body}" stroke="${p.halo}" stroke-width="1.5" paint-order="stroke"></circle>\n` +
        `<circle cx="16" cy="39" r="1.3" fill="${p.halo}"></circle>\n`);
}
function disc(p) {
    return `<circle cx="16" cy="12" r="11" fill="${p.body}" stroke="${p.halo}" stroke-width="2" paint-order="stroke"></circle>\n`;
}
function pickupFace(p) {
    return `<circle cx="16" cy="12" r="7" fill="${p.core}"></circle>\n<circle cx="16" cy="12" r="4" fill="${PICKUP_GREEN}"></circle>\n`;
}
const CHECK_SQUARES = [
    ['7.5', '3.5'], ['16', '3.5'], ['11.75', '7.75'], ['20.25', '7.75'], ['7.5', '12'], ['16', '12'], ['11.75', '16.25'], ['20.25', '16.25'],
];
function dropoffFace(p, theme) {
    const id = `ck${theme}`;
    const squares = CHECK_SQUARES.map(([x, y]) => `<rect x="${x}" y="${y}" width="4.25" height="4.25" fill="${p.checkSquare}"></rect>`).join('');
    return `<clipPath id="${id}"><circle cx="16" cy="12" r="8.5"></circle></clipPath><circle cx="16" cy="12" r="8.5" fill="${p.checkGround}"></circle><g clip-path="url(#${id})">${squares}</g>\n`;
}
const STOP_WITH_TAIL = 'M7 2H17A5 5 0 0 1 22 7V17A5 5 0 0 1 17 22H14.5L12 26.5L9.5 22H7A5 5 0 0 1 2 17V7A5 5 0 0 1 7 2Z';
const STOP_HEAD = 'M7 2H17A5 5 0 0 1 22 7V17A5 5 0 0 1 17 22H7A5 5 0 0 1 2 17V7A5 5 0 0 1 7 2Z';
function svgOpen(width, height, viewBox) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${viewBox}" fill="none">\n`;
}
/**
 * The marker's SVG markup for a theme: with its stem for the map (anchor: {@link mapMarkerAnchor}), or `head: true` for the
 * bare disc / square of a list row.
 */
export function mapMarkerSvg(kind, theme, options = {}) {
    const p = PALETTES[theme];
    const head = options.head === true;
    if (kind === 'stop') {
        const n = Math.max(1, Math.floor(options.n ?? 1));
        const font = options.fontFamily ?? DESIGN_FONT;
        const body = head
            ? `<path d="${STOP_HEAD}" fill="${p.body}" stroke="${p.halo}" stroke-width="1.5" paint-order="stroke" stroke-linejoin="round"></path>\n`
            : `<ellipse cx="12" cy="28" rx="5" ry="1.6" fill="#000" opacity=".16"></ellipse>\n<path d="${STOP_WITH_TAIL}" fill="${p.body}" stroke="${p.halo}" stroke-width="1.5" paint-order="stroke" stroke-linejoin="round"></path>\n`;
        const digits = `<text x="12" y="16.3" font-family="${font}" font-size="${n > 9 ? 10 : 12}" font-weight="700" text-anchor="middle" fill="${p.digit}">${n}</text>\n`;
        return head ? `${svgOpen(21.5, 21.5, '1.25 1.25 21.5 21.5')}${body}${digits}</svg>` : `${svgOpen(24, 30, '0 0 24 30')}${body}${digits}</svg>`;
    }
    const face = kind === 'pickup' ? pickupFace(p) : dropoffFace(p, theme);
    return head ? `${svgOpen(24, 24, '4 0 24 24')}${disc(p)}${face}</svg>` : `${svgOpen(32, 44, '0 0 32 44')}${stem(p)}${disc(p)}${face}</svg>`;
}
/** The marker as a `data:` URI (for an `<img>` / CSS background on the web). */
export function mapMarkerDataUri(kind, theme, options = {}) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(mapMarkerSvg(kind, theme, options))}`;
}
//# sourceMappingURL=map-markers.js.map